import axios from "axios";

export interface AdminUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  isAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  processor?: string;
  paddleId?: string;
  aiImage?: number;
  aiImageLimit?: number;
  aiImageUpdatedAt?: string;
}

export interface AdminSubscription {
  _id: string;
  userId: string;
  userEmail?: string;
  planId: any;
  status: string;
  processor: string;
  startDate: string;
  rebillDate?: string;
  endDate?: string;
  autoRenew: boolean;
  paddleSubscriptionId?: string;
  features?: any;
  createdAt: string;
  updatedAt: string;
  creditLedgerCount?: number;
}

export interface AdminCreditLedger {
  _id: string;
  userId: string;
  userEmail?: string;
  creditType: string;
  value: number;
  type: "credit" | "debit";
  description?: string;
  title?: string;
  createdAt: string;
  expiresAt?: string;
  totalCreditsUsed?: number;
  paddleSubscriptionId?: string | null;
  subscription?: {
    _id: string;
    processor: string;
    status: string;
    paddleSubscriptionId?: string;
  } | null;
}

export interface AdminPaddleSyncChange {
  field: string;
  current: unknown;
  next: unknown;
  reason?: string;
}

export interface AdminPaddleSyncSideEffect {
  label: string;
  current?: unknown;
  next: unknown;
  reason?: string;
}

export interface AdminPaddleSyncPriceSummary {
  priceId: string;
  productId: string | null;
  name: string;
  kind: "primary" | "addon" | "hidden" | null;
  status: string | null;
  quantity: number;
  recurring: boolean;
  billingInterval: string | null;
  articleCredits: number;
  aiImageCredits: number;
  afterEnd: string | null;
  disablePig: boolean;
  shortName: string | null;
  descriminators: string | null;
  previouslyBilledAt: string | null;
  nextBilledAt: string | null;
  trialStartsAt: string | null;
  trialEndsAt: string | null;
  effectiveStartsAt: string | null;
  effectiveEndsAt: string | null;
  customData: Record<string, unknown>;
}

export interface AdminPaddleSyncPreview {
  mode: "active" | "cancelled";
  paddleStatus: string;
  summary: string;
  warnings: string[];
  subscriptionChanges: AdminPaddleSyncChange[];
  userChanges: AdminPaddleSyncChange[];
  sideEffects: AdminPaddleSyncSideEffect[];
  metadata: {
    billingInterval: string | null;
    currentBillingPeriodStart: string | null;
    currentBillingPeriodEnd: string | null;
    nextBilledAt: string | null;
    articleCredits: number;
    aiImageCredits: number;
    hasUnlimitedImages: boolean;
    willResetAiImageUsage: boolean;
    keepAccessUntil: string | null;
    validitySourcePriceId: string | null;
    validitySourcePriceName: string | null;
    priceSummaries: AdminPaddleSyncPriceSummary[];
  };
}

export interface AdminPaddleUserSyncPreviewItem {
  subscriptionId: string;
  localPaddleSubscriptionId: string | null;
  matchedPaddleSubscriptionId: string | null;
  matchedCustomerId: string | null;
  matchedVia: "subscription_id" | "paddle_id" | "email" | null;
  warning?: string | null;
  preview: AdminPaddleSyncPreview | null;
  error?: string;
}

export interface AdminPaddleUserSyncPreview {
  summary: string;
  warnings: string[];
  subscriptionPreviews: AdminPaddleUserSyncPreviewItem[];
  unmatchedPaddleSubscriptions: Array<{
    paddleSubscriptionId: string;
    customerId: string | null;
    status: string;
    keepAccessUntil: string | null;
    billingInterval: string | null;
  }>;
  metadata: {
    totalLocalSubscriptions: number;
    matchedLocalSubscriptions: number;
    unmatchedLocalSubscriptions: number;
    unmatchedPaddleSubscriptions: number;
  };
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CreditBalances {
  ARTICLE: number;
  NICHE_TOOL: number;
  HEADLINE_TOOL: number;
  KEYWORD_TOOL: number;
}

export interface UserRateLimits {
  userId: string;
  userEmail: string;
  totalImagesGenerated: number;
  imagesInLast24h: number;
  imagesInLast30Days?: number;
  limit: number;
  remaining: number;
  isLimitReached: boolean;
  resetInfo: {
    resetsAt: string;
    resetsInMs: number;
    resetsInHours: number;
    resetsInMinutes: number;
  } | null;
}

export interface UserAiUsageDay {
  date: string;
  cost: number;
  requests: number;
  words: number;
}

export interface UserAiUsage {
  days: number;
  usage: UserAiUsageDay[];
  totals: { cost: number; requests: number; words: number };
}

class AdminAPI {
  // User management
  async getUsers(params?: { page?: number; limit?: number; search?: string; status?: string; sortBy?: "createdAt" | "aiImage" }) {
    const response = await axios.get("/api/admin/users", { params });
    return response.data.result;
  }

  async updateUser(userId: string, updates: Partial<AdminUser>) {
    const response = await axios.put(`/api/admin/users/${userId}`, { userId, updates });
    return response.data.result;
  }

  async getUserSubscriptions(userId: string) {
    const response = await axios.get(`/api/admin/users/${userId}/subscriptions`);
    return response.data.result;
  }

  async getUserCredits(userId: string, page = 1, limit = 50) {
    const response = await axios.get(`/api/admin/users/${userId}/credits`, {
      params: { page, limit },
    });
    return response.data.result;
  }

  async addCredits(
    userId: string,
    creditType: string,
    amount: number,
    description: string,
    expiresAt?: string
  ) {
    const response = await axios.post(`/api/admin/users/${userId}/credits/add`, {
      userId,
      creditType,
      amount,
      description,
      expiresAt,
    });
    return response.data.result;
  }

  async removeCredits(
    userId: string,
    creditType: string,
    amount: number,
    description: string
  ) {
    const response = await axios.post(`/api/admin/users/${userId}/credits/remove`, {
      userId,
      creditType,
      amount,
      description,
    });
    return response.data.result;
  }

  // Subscription management
  async getAllSubscriptions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    processor?: string;
  }) {
    const response = await axios.get("/api/admin/subscriptions", { params });
    return response.data.result;
  }

  async updateSubscription(subscriptionId: string, updates: Partial<AdminSubscription>) {
    const response = await axios.put(`/api/admin/subscriptions/${subscriptionId}`, {
      subscriptionId,
      updates,
    });
    return response.data.result;
  }

  async deleteSubscription(subscriptionId: string) {
    const response = await axios.delete(`/api/admin/subscriptions/${subscriptionId}`);
    return response.data.result;
  }

  async getPaddleSubscriptionSyncPreview(subscriptionId: string) {
    const response = await axios.get(`/api/admin/subscriptions/${subscriptionId}/paddle-sync-preview`);
    return response.data.result;
  }

  async syncPaddleSubscription(subscriptionId: string) {
    const response = await axios.post(`/api/admin/subscriptions/${subscriptionId}/paddle-sync`);
    return response.data.result;
  }

  async getPaddleUserSyncPreview(userId: string) {
    const response = await axios.get(`/api/admin/users/${userId}/paddle-sync-preview`);
    return response.data.result;
  }

  async syncPaddleUser(userId: string) {
    const response = await axios.post(`/api/admin/users/${userId}/paddle-sync`);
    return response.data.result;
  }

  async generateLoginUrl(userId: string) {
    const response = await axios.post(`/api/admin/users/${userId}/generate-login-url`);
    return response.data.result;
  }

  async deleteCreditLedgerEntry(ledgerId: string) {
    const response = await axios.delete(`/api/admin/credits/${ledgerId}`);
    return response.data.result;
  }

  async deleteAllCreditLedgerEntries(userId: string) {
    const response = await axios.delete(`/api/admin/users/${userId}/credits`);
    return response.data.result;
  }

  async updateCreditLedgerEntry(ledgerId: string, updates: {
    value?: number;
    paddleSubscriptionId?: string;
    subscriptionId?: string | null;
    description?: string;
    expiresAt?: string | null;
  }) {
    const response = await axios.put(`/api/admin/credits/${ledgerId}`, { updates });
    return response.data.result;
  }

  async getUser(userId: string) {
    const response = await axios.get(`/api/admin/users/${userId}`);
    return response.data.result;
  }

  async updateSubscriptionImageCredits(subscriptionId: string, used: number, available: number) {
    const response = await axios.put(`/api/admin/subscriptions/${subscriptionId}`, {
      subscriptionId,
      updates: {
        features: {
          AI_IMAGE_USAGE: {
            used: used,
            limit: available
          }
        }
      }
    });
    return response.data.result;
  }

  async createUser(userData: {
    email: string;
    firstName?: string;
    lastName?: string;
    password: string;
    isAdmin?: boolean;
  }) {
    const response = await axios.post('/api/admin/users/create', userData);
    return response.data.result;
  }

  async createSubscription(userId: string, subscriptionData: {
    processor: string;
    status: string;
    planId?: string;
    startDate: string;
    rebillDate?: string;
    endDate?: string;
    autoRenew: boolean;
    features: any;
  }) {
    const response = await axios.post(`/api/admin/users/${userId}/subscriptions/create`, subscriptionData);
    return response.data.result;
  }

  async getPlans() {
    const response = await axios.get('/api/admin/plans');
    return response.data.result;
  }

  async getUserRateLimits(userId: string) {
    const response = await axios.get(`/api/admin/users/${userId}/rate-limits`);
    return response.data.result;
  }

  async getUserAiUsage(userId: string) {
    const response = await axios.get(`/api/admin/users/${userId}/ai-usage`);
    return response.data.result;
  }
}

export default new AdminAPI();
